import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

function prepareCartFeedback(button) {
  const target = button?.querySelector('[data-cart-feedback]');
  const template = document.getElementById('clendo-cart-feedback-template');
  if (target && !target.firstElementChild && template) target.append(template.content.cloneNode(true));
}

// Capture runs before the existing main-product click/submit handlers.
document.addEventListener('click', event => prepareCartFeedback(event.target.closest('button')), { capture: true });
document.addEventListener('submit', event => prepareCartFeedback(event.submitter || event.target.querySelector('button[type="submit"]')), { capture: true });

class StudioCrossSelling extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.panelRequests = new WeakMap();
    this.warmingStreams = false;
    this.quantityControls = new WeakSet();
    this.quantityInputs = new WeakSet();
    if (this.closest('.boost-sd__product-item')) {
      // Keep app card navigation from swallowing the independent card controls.
      for (const type of ['click', 'change', 'submit']) this.addEventListener(type, event => event.stopPropagation(), { signal });
    }
    this.addEventListener('change', event => {
      const select = event.target.closest('select[name="id"]');
      if (select) this.updateVariant(select);
    }, { signal });
    this.addEventListener('submit', event => {
      if (!event.target.matches('.cs-crosssell-form')) return;
      event.preventDefault();
      this.addToCart(event.target);
    }, { signal });
    this.addEventListener('click', event => {
      const tab = event.target.closest('[data-crosssell-tab]');
      if (tab) { this.streamsInView = true; this.activateTab(tab.dataset.crosssellTab); return; }
      const button = event.target.closest('[data-scroll]');
      if (!button) return;
      const track = button.closest('.cs-crosssell-stream').querySelector('.cs-crosssell-track');
      track.scrollBy({ left: Number(button.dataset.scroll) * track.clientWidth * .9, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal });
    const warmIntent = event => {
      const tab = event.target.closest('[data-crosssell-tab]');
      const panel = tab && document.getElementById(tab.dataset.crosssellTab);
      if (panel && this.contains(panel)) void this.loadPanel(panel);
    };
    this.addEventListener('pointerover', warmIntent, { passive: true, signal });
    this.addEventListener('focusin', warmIntent, { signal });
    this.addEventListener('keydown', event => {
      const tab = event.target.closest('[data-crosssell-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const tabs = [...this.querySelectorAll('[data-crosssell-tab]')];
      const index = tabs.indexOf(tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + tabs.length) % tabs.length;
      event.preventDefault();
      this.streamsInView = true;
      this.activateTab(tabs[next].dataset.crosssellTab);
      tabs[next].focus();
    }, { signal });
    this.mobileStreams = matchMedia('(max-width:749px)');
    this.mobileStreams.addEventListener('change', () => { this.syncStreamLayout(); this.setupQuantities(); this.updateArrows(); }, { signal });
    this.setupPlaceholders();
    this.setupTabs();
    this.setupQuantities();
    this.setupTracks();
    void this.loadStreamCounts();
    if (this.querySelector('[data-deferred-stream]')) {
      const prepareNearbyStreams = () => {
        if (this.streamsInView) return;
        const bounds = this.getBoundingClientRect();
        if (!bounds.height || bounds.top > window.innerHeight + 650 || bounds.bottom < -650) return;
        this.streamsInView = true;
        this.observer?.disconnect();
        this.loadVisibleStreams();
      };
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.observer.disconnect();
          this.streamsInView = true;
          this.loadVisibleStreams();
        }
      }, { rootMargin: '650px' });
      this.observer.observe(this);
      // Overflow-clipped ancestors can prevent an observer's expanded margin from taking effect.
      window.addEventListener('scroll', prepareNearbyStreams, { passive: true, signal });
      window.addEventListener('resize', prepareNearbyStreams, { passive: true, signal });
      prepareNearbyStreams();
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.panelObserver?.disconnect();
    cancelAnimationFrame(this.arrowsFrame);
    this.arrowsFrame = null;
  }

  setupTabs() {
    const list = this.querySelector('.cs-crosssell-tabs');
    if (!list) return;
    const panels = [...this.querySelectorAll('[data-crosssell-panel]')];
    const available = panels.filter(panel => this.panelAvailable(panel));
    const selected = available.some(panel => panel.id === this.activePanel) ? this.activePanel : available[0]?.id;
    const focusedPanel = list.contains(document.activeElement) ? document.activeElement.dataset.crosssellTab : null;
    const buttons = available.map(panel => {
      const button = [...list.children].find(item => item.dataset.crosssellTab === panel.id) || document.createElement('button');
      button.type = 'button'; button.id = panel.id + '-tab';
      button.dataset.crosssellTab = panel.id;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panel.id);
      const label = document.createElement('span');
      label.textContent = panel.dataset.streamLabel || panel.querySelector('h3')?.textContent;
      const count = document.createElement('span');
      count.className = 'cs-crosssell-tab-count';
      count.setAttribute('aria-hidden', 'true');
      const cards = panel.querySelectorAll('.cs-crosssell-card').length;
      const total = cards || Number(panel.dataset.streamCount || 0);
      count.textContent = total;
      button.replaceChildren(label);
      if (total > 0) button.append(count);
      return button;
    });
    // Preserve the actual tab nodes and keyboard focus while counts arrive.
    for (const button of [...list.children]) if (!buttons.includes(button)) button.remove();
    buttons.forEach((button, index) => {
      if (list.children[index] !== button) list.insertBefore(button, list.children[index] || null);
    });
    list.hidden = !available.length;
    this.classList.toggle('cs-cross-selling--tabbed', available.length > 0);
    for (const panel of panels) {
      panel.hidden = panel.id !== selected;
      if (available.includes(panel)) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', panel.id + '-tab');
      }
    }
    if (selected) this.activateTab(selected);
    if (focusedPanel) buttons.find(button => button.dataset.crosssellTab === focusedPanel)?.focus();
  }

  activateTab(panelId) {
    const previous = [...this.querySelectorAll('[data-crosssell-panel]')].find(panel => !panel.hidden);
    if (this.activePanel !== panelId && !this.mobileStreams?.matches && previous?.querySelector('.cs-crosssell-card')) {
      const height = previous.getBoundingClientRect().height;
      if (height > 0) this.style.setProperty('--cs-stream-loading-height', `${height}px`);
    }
    this.activePanel = panelId;
    for (const button of this.querySelectorAll('[data-crosssell-tab]')) {
      const active = button.dataset.crosssellTab === panelId;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    }
    this.syncStreamLayout();
    this.updateArrows();
  }

  syncStreamLayout() {
    const list = this.querySelector('.cs-crosssell-tabs');
    if (!list) return;
    const mobile = this.mobileStreams?.matches ?? matchMedia('(max-width:749px)').matches;
    const panels = [...this.querySelectorAll('[data-crosssell-panel]')];
    list.hidden = mobile || !panels.some(panel => this.panelAvailable(panel));
    for (const panel of panels) {
      const populated = this.panelAvailable(panel);
      panel.hidden = !populated || (!mobile && panel.id !== this.activePanel);
      if (mobile || !populated) {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      } else {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', panel.id + '-tab');
      }
    }
    this.loadVisibleStreams();
  }

  panelAvailable(panel) {
    return Boolean(panel.querySelector('.cs-crosssell-card') || (panel.hasAttribute('data-deferred-stream') && panel.dataset.streamCount !== '0') || panel.hasAttribute('data-stream-fallback'));
  }

  async loadStreamCounts() {
    const signal = this.controller.signal;
    const panels = [...this.querySelectorAll('[data-deferred-stream]:not([data-stream-count])')];
    await Promise.allSettled(panels.map(async panel => {
      try {
        const collection = panel.hasAttribute('data-collection-stream');
        const url = new URL(collection ? panel.dataset.collectionUrl : this.dataset.recommendationUrl, window.location.origin);
        url.searchParams.set('section_id', 'studio-crosssell-counts');
        if (collection) url.searchParams.set('sort_by', 'best-selling');
        const response = await fetch(url, { signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const metadata = html.querySelector('[data-crosssell-product-ids]');
        if (!metadata || signal.aborted || !panel.hasAttribute('data-deferred-stream')) return;
        const ids = JSON.parse(metadata.textContent);
        if (!Array.isArray(ids) || !ids.every(id => typeof id === 'number' || typeof id === 'string')) return;
        const limit = collection ? 8 : Number(this.dataset.streamLimit) || 7;
        const count = Math.min(limit, ids.filter(id => String(id) !== this.dataset.currentProductId).length);
        panel.dataset.streamCount = String(count);
        this.setupTabs();
        this.hidden = ![...this.querySelectorAll('[data-crosssell-panel]')].some(item => this.panelAvailable(item));
      } catch {
        // A failed count request must never prevent opening the actual stream.
      }
    }));
  }

  loadVisibleStreams() {
    this.panelObserver?.disconnect();
    if (!this.streamsInView) return;
    if (this.mobileStreams.matches) {
      this.panelObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.panelObserver.unobserve(entry.target);
          void this.loadPanel(entry.target);
        }
      }, { rootMargin: '250px' });
      for (const panel of this.querySelectorAll('[data-deferred-stream]')) this.panelObserver.observe(panel);
    } else {
      const active = [...this.querySelectorAll('[data-crosssell-panel]')].find(panel => panel.id === this.activePanel);
      if (active) void this.loadPanel(active);
      void this.warmStreams();
    }
  }

  setupPlaceholders() {
    for (const placeholder of this.querySelectorAll('.cs-crosssell-placeholder')) {
      if (placeholder.querySelector('.cs-crosssell-skeletons')) continue;
      const track = document.createElement('div');
      track.className = 'cs-crosssell-track cs-crosssell-skeletons';
      track.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < 6; index++) {
        const card = document.createElement('div');
        card.className = 'cs-crosssell-skeleton';
        for (const part of ['image', 'title', 'variant', 'price', 'button']) {
          const block = document.createElement('span');
          block.className = `cs-crosssell-skeleton__${part}`;
          card.append(block);
        }
        track.append(card);
      }
      placeholder.append(track);
    }
  }

  async warmStreams() {
    // One background stream at a time; deliberate tab interaction takes priority.
    if (this.warmingStreams || this.mobileStreams.matches || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
    this.warmingStreams = true;
    const signal = this.controller.signal;
    try {
      const active = [...this.querySelectorAll('[data-crosssell-panel]')].find(panel => panel.id === this.activePanel);
      if (active) await this.loadPanel(active);
      for (const panel of this.querySelectorAll('[data-deferred-stream]')) {
        if (signal.aborted || this.mobileStreams.matches) break;
        await this.loadPanel(panel);
      }
    } finally {
      if (!signal.aborted) this.warmingStreams = false;
    }
  }

  loadPanel(panel) {
    const pending = this.panelRequests.get(panel);
    if (pending) return pending;
    if (!panel.hasAttribute('data-deferred-stream') || panel.dataset.streamCount === '0') return Promise.resolve();
    const request = this.fetchPanel(panel);
    this.panelRequests.set(panel, request);
    return request;
  }

  async fetchPanel(panel) {
    panel.dataset.loading = 'true';
    panel.setAttribute('aria-busy', 'true');
    const signal = this.controller.signal;
    try {
      if (panel.hasAttribute('data-collection-stream')) await this.loadCollectionStream(panel);
      else await this.loadRecommendations();
      if (signal.aborted) return;
      // Prepare only the first visible row of a warmed desktop tab, not the entire carousel.
      if (!this.mobileStreams.matches) {
        const width = this.querySelector('.cs-cross-selling__inner')?.clientWidth || 220;
        const columns = Math.max(1, Math.min(6, Math.floor((width + 16) / 236)));
        for (const image of [...panel.querySelectorAll('img.cs-crosssell-img')].slice(0, columns)) {
          image.loading = 'eager';
          image.setAttribute('fetchpriority', panel.hidden ? 'low' : 'auto');
        }
      }
      panel.removeAttribute('data-deferred-stream');
      panel.querySelector('.cs-crosssell-placeholder')?.remove();
    } catch (error) {
      if (signal.aborted) return;
      panel.removeAttribute('data-deferred-stream');
      // Keep a real collection link if a nonessential recommendation request fails.
      if (panel.dataset.collectionUrl) {
        panel.setAttribute('data-stream-fallback', '');
        panel.querySelector('[role="status"]')?.remove();
        panel.querySelector('.cs-crosssell-skeletons')?.remove();
      } else panel.replaceChildren();
    } finally {
      delete panel.dataset.loading;
      panel.removeAttribute('aria-busy');
      if (!signal.aborted) {
        this.setupTabs();
        this.setupQuantities();
        this.setupTracks();
        this.hidden = ![...this.querySelectorAll('[data-crosssell-panel]')].some(item => this.panelAvailable(item));
      }
    }
  }

  setupTracks() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.updateArrows());
    for (const track of this.querySelectorAll('.cs-crosssell-track')) {
      this.resizeObserver.observe(track);
      if (!track.dataset.bound) {
        track.dataset.bound = 'true';
        track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal: this.controller.signal });
      }
    }
    this.updateArrows();
  }

  updateArrows() {
    if (this.arrowsFrame != null) return;
    this.arrowsFrame = requestAnimationFrame(() => {
      this.arrowsFrame = null;
      if (!this.isConnected) return;
      // Read visible tracks together before changing any controls. Hidden tabs
      // and mobile carousels need no layout measurement.
      const states = [...this.querySelectorAll('.cs-crosssell-stream')].map(stream => {
        const track = stream.querySelector('.cs-crosssell-track');
        const arrows = stream.querySelector('.cs-crosssell-arrows');
        if (!track || !arrows) return null;
        const hidden = this.mobileStreams.matches || Boolean(stream.closest('[hidden]'));
        const width = hidden ? 0 : track.clientWidth;
        const total = hidden ? 0 : track.scrollWidth;
        const left = hidden ? 0 : Math.abs(track.scrollLeft);
        return { arrows, hidden: hidden || total <= width + 2,
          previous: stream.querySelector('[data-scroll="-1"]'),
          next: stream.querySelector('[data-scroll="1"]'),
          atStart: left < 2, atEnd: left + width >= total - 2 };
      }).filter(Boolean);
      for (const state of states) {
        if (state.arrows.hidden !== state.hidden) state.arrows.hidden = state.hidden;
        if (state.previous && state.previous.disabled !== state.atStart) state.previous.disabled = state.atStart;
        if (state.next && state.next.disabled !== state.atEnd) state.next.disabled = state.atEnd;
      }
    });
  }


  async loadCollectionStream(panel) {
    try {
      const url = new URL(panel.dataset.collectionUrl, window.location.origin);
      url.searchParams.set('sort_by', 'best-selling');
      url.searchParams.set('section_id', 'studio-crosssell-collection');
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Collection unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('.cs-crosssell-stream');
      if (incoming) {
        const cards = [...incoming.querySelectorAll('.cs-crosssell-card')];
        let count = 0;
        for (const card of cards) {
          if (card.dataset.productId === this.dataset.currentProductId || count >= 8) card.remove();
          else count++;
        }
        if (count) {
          // Scope fetched control IDs to this panel, including labels and ARIA references.
          const ids = new Map();
          for (const element of incoming.querySelectorAll('[id]')) {
            const oldId = element.getAttribute('id');
          const newId = panel.id + '-' + oldId;
          element.setAttribute('id', newId);
          ids.set(oldId, newId);
          }
          for (const element of [incoming, ...incoming.querySelectorAll('[for], [aria-labelledby], [aria-controls], [aria-describedby]')]) {
            for (const attribute of ['for', 'aria-labelledby', 'aria-controls', 'aria-describedby']) {
              if (!element.hasAttribute(attribute)) continue;
              element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
            }
          }
          panel.replaceChildren(incoming);
        }
      }
      panel.dataset.loaded = 'true';
    } catch (error) {
      throw error;
    }
  }

  async loadRecommendations() {
    try {
      const url = new URL(this.dataset.recommendationUrl, window.location.origin);
      url.searchParams.set('section_id', 'studio-crosssell-recommendations');
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('[data-frequent-stream]');
      const target = this.querySelector('[data-frequent-stream]');
      const limit = Number(this.dataset.streamLimit) || 7;
      for (const card of [...(incoming?.querySelectorAll('.cs-crosssell-card') || [])].slice(limit)) card.remove();
      if (incoming && target) {
        const ids = new Map();
        for (const element of incoming.querySelectorAll('[id]')) {
          const oldId = element.getAttribute('id');
          const newId = target.id + '-' + oldId;
          element.setAttribute('id', newId);
          ids.set(oldId, newId);
        }
        for (const element of incoming.querySelectorAll('[for], [aria-labelledby], [aria-controls], [aria-describedby]')) {
          for (const attribute of ['for', 'aria-labelledby', 'aria-controls', 'aria-describedby']) {
            if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
          }
        }
      }
      if (incoming && target) target.replaceChildren(...incoming.childNodes);
      if (target) target.dataset.loaded = 'true';

    } catch (error) {
      throw error;
    }
  }

  updateVariant(select) {
    const option = select.selectedOptions[0];
    if (!option) return;
    const card = select.closest('.cs-crosssell-card');
    card.querySelector('[data-card-price]').textContent = option.dataset.price;
    const comparePrice = option.dataset.compare || '';
    card.querySelector('[data-card-compare]').textContent = comparePrice;
    card.querySelector('[data-card-compare-wrap]').hidden = !comparePrice;
    card.querySelector('.cs-crosssell-pricing').dataset.discounted = String(Boolean(comparePrice));
    card.querySelector('[data-card-unit]').textContent = option.dataset.unit;
    const quantity = card.querySelector('[name="quantity"]');
    quantity.min = option.dataset.min;
    quantity.step = option.dataset.step;
    quantity.max = option.dataset.max;
    if (!quantity.checkValidity()) quantity.value = option.dataset.min;
    this.syncQuantity(quantity);
    const purchase = card.querySelector('[data-listing-price-limit]');
    if (purchase) {
      const detailsOnly = Number(option.dataset.priceAmount) >= Number(purchase.dataset.listingPriceLimit);
      purchase.toggleAttribute('data-details-only', detailsOnly);
      purchase.querySelector('button[type="submit"]').disabled = detailsOnly || option.dataset.available !== 'true';
    }
    if (this.classList.contains('studio-listing-card')) {
      for (const link of card.querySelectorAll('.cs-crosssell-title, .cs-crosssell-image, .cs-crosssell-rating, .cs-card-details, .studio-listing-mobile-link')) {
        const url = new URL(link.href); url.searchParams.set('variant', option.value); link.href = url.href;
      }
    }
    const image = card.querySelector('.cs-crosssell-img');
    if (image && option.dataset.image) { image.src = option.dataset.image; image.removeAttribute('srcset'); }
    card.querySelector('.cs-crosssell-status').textContent = '';
  }


  setupQuantities() {
    const skip = this.classList.contains('studio-listing-card') && this.mobileStreams.matches;
    for (const input of this.querySelectorAll('.cs-crosssell-quantity input')) {
      const root = input.parentElement;
      if (skip) {
        root.querySelector('select')?.remove();
        root.classList.remove('is-enhanced', 'is-custom');
        continue;
      }
      let select = root.querySelector('select');
      if (!select) {
        select = document.createElement('select');
        select.className = 'cs-crosssell-quantity-select';
        select.setAttribute('aria-label', input.getAttribute('aria-label') || input.labels[0]?.textContent || 'Quantity');
        root.append(select);
        root.classList.add('is-enhanced');
      }
      if (!this.quantityControls.has(select)) {
        this.quantityControls.add(select);
        // Expand before the native picker opens, for mouse, touch and keyboard.
        for (const type of ['pointerdown', 'focus', 'keydown']) {
          select.addEventListener(type, () => this.syncQuantity(input, true), { signal: this.controller.signal });
        }
        select.addEventListener('change', () => {
          if (select.value === 'custom') {
            root.classList.add('is-custom');
            input.focus(); input.select();
          } else {
            root.classList.remove('is-custom');
            input.value = select.value;
          }
        }, { signal: this.controller.signal });
      }
      if (!this.quantityInputs.has(input)) {
        this.quantityInputs.add(input);
        input.addEventListener('blur', () => this.syncQuantity(input), { signal: this.controller.signal });
      }
      this.syncQuantity(input);
    }
  }

  syncQuantity(input, expand = false) {
    const select = input.parentElement.querySelector('select');
    if (!select) return;
    const min = Math.max(1, Number(input.min) || 1), step = Number(input.step) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const key = [min, step, max].join(':');
    if (select.dataset.constraints !== key) {
      delete select.dataset.expanded;
      select.dataset.constraints = key;
    }
    if (expand && !select.dataset.expanded) {
      const options = [];
      for (let value = min; value < 50 && value <= max; value += step) options.push(new Option(String(value), String(value)));
      if (max >= 50) options.push(new Option('50+', 'custom'));
      select.replaceChildren(...options);
      select.dataset.expanded = 'true';
    } else if (!select.dataset.expanded && (select.options.length !== 1 || select.options[0].value !== input.value)) {
      // One initial option preserves the control's appearance without 50 DOM nodes per card.
      select.replaceChildren(new Option(input.value, input.value));
    }
    const custom = ![...select.options].some(option => option.value === input.value);
    input.parentElement.classList.toggle('is-custom', custom);
    select.value = custom ? 'custom' : input.value;
  }

  async addToCart(form) {
    const button = form.querySelector('button[type="submit"]');
    if (!button || button.disabled || form.querySelector('[data-listing-price-limit][data-details-only]') || !form.reportValidity()) return;
    prepareCartFeedback(button);
    const labelElement = button.querySelector('.cs-card-cart-label') || button.querySelector('.add-to-cart-text');
    const label = labelElement.textContent;
    const status = form.querySelector('.cs-crosssell-status');
    status.textContent = ''; status.removeAttribute('data-error');
    const body = new FormData(form);
    const quantity = Number(body.get('quantity'));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean);
    body.set('sections', [...new Set(sectionIds)].slice(0,5).join(','));
    body.set('sections_url', window.location.pathname);
    button.disabled = true; button.setAttribute('aria-busy', 'true');
    labelElement.textContent = this.dataset.loadingLabel;
    const pending = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(body.get('id')), quantity }], promise: pending.promise }));
    let added = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorLabel);
      added = true;
      button.removeAttribute('aria-busy');
      labelElement.textContent = label;
      button.dataset.added = 'true';
      const resetFeedback = async () => {
        await yieldToMainThread();
        await onAnimationEnd(button);
        setTimeout(() => button.removeAttribute('data-added'), 800);
      };
      void resetFeedback();
      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.errorLabel);
      const cart = await cartResponse.json();
      pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: quantity, source: 'studio-cross-selling', sourceId: form.getAttribute('id'), productId: form.closest('[data-product-id]').dataset.productId, sections: result.sections, didError: false } });
      status.textContent = this.dataset.addedLabel;
    } catch (error) {
      pending.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'CART_UPDATE_ERROR' }));
      status.textContent = added ? this.dataset.addedLabel : error.message || this.dataset.errorLabel;
      if (!added) status.setAttribute('data-error', '');
    } finally {
      button.disabled = false; button.removeAttribute('aria-busy'); labelElement.textContent = label;
    }
  }
}

if (!customElements.get('studio-cross-selling')) customElements.define('studio-cross-selling', StudioCrossSelling);
